import { useState } from "react";
import { useFocusTrap } from "../../../hooks/useFocusTrap.js";
import { useModalStore } from "../../../stores/modalStore";
import { useServerStore } from "../../../stores/serverStore";

const EXPIRY_OPTIONS = [
  { label: '30 minutes', value: 30 * 60 },
  { label: '1 hour',     value: 60 * 60 },
  { label: '6 hours',    value: 6 * 60 * 60 },
  { label: '12 hours',   value: 12 * 60 * 60 },
  { label: '1 day',      value: 24 * 60 * 60 },
  { label: '7 days',     value: 7 * 24 * 60 * 60 },
  { label: 'Never',      value: 0 },
];

const MAX_USE_OPTIONS = [
  { label: 'No limit', value: 0 },
  { label: '1 use',    value: 1 },
  { label: '5 uses',   value: 5 },
  { label: '10 uses',  value: 10 },
  { label: '25 uses',  value: 25 },
  { label: '50 uses',  value: 50 },
  { label: '100 uses', value: 100 },
];

function generateCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export function InviteModal() {
  const { type, isOpen, onClose } = useModalStore();
  const currentServerId = useServerStore(s => s.currentServerId);

  const [copied,   setCopied]   = useState(false);
  const [expiry,   setExpiry]   = useState(7 * 24 * 60 * 60);
  const [maxUses,  setMaxUses]  = useState(0);
  const [code,     setCode]     = useState(() => generateCode());
  const [creating, setCreating] = useState(false);
  const isModalOpen = isOpen && type === 'invite';
  const trapRef = useFocusTrap(isModalOpen);

  if (!isModalOpen) return null;

  const origin    = typeof window !== 'undefined' ? window.location.origin : '';
  const inviteUrl = `${origin}/invite/${code}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(inviteUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGenerate = async () => {
    setCreating(true);
    const newCode = generateCode();

    if (currentServerId && !currentServerId.startsWith('demo-')) {
      try {
        await fetch(`/api/servers/${currentServerId}/invites`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
          body: JSON.stringify({ code: newCode, expiresIn: expiry || null, maxUses: maxUses || null }),
        });
      } catch (_) {}
    }

    setCode(newCode);
    setCopied(false);
    setCreating(false);
  };

  const expiryLabel  = EXPIRY_OPTIONS.find(o => o.value === expiry)?.label  || '7 days';
  const maxUseLabel  = MAX_USE_OPTIONS.find(o => o.value === maxUses)?.label || 'No limit';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={onClose}
    >
      <div ref={trapRef} role="dialog" aria-modal="true" aria-label="Invite People"
        style={{ background: 'rgb(var(--aethex-panel))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, width: 460, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Invite People</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>Share this link to invite friends</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: '20px' }}>
          {/* Link row */}
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
            Server invite link
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, background: 'rgb(var(--aethex-bg))', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '9px 12px', fontSize: 13, color: 'rgba(255,255,255,0.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', userSelect: 'all' }}>
              {inviteUrl}
            </div>
            <button onClick={handleCopy}
              style={{ padding: '9px 16px', borderRadius: 6, background: copied ? '#22c55e' : 'rgb(var(--aethex-primary))', border: 'none', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'background 0.2s', whiteSpace: 'nowrap' }}>
              {copied ? '✓ Copied!' : 'Copy'}
            </button>
          </div>

          {/* Options */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
            {/* Expiry */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Expire after</div>
              <select value={expiry} onChange={e => setExpiry(Number(e.target.value))}
                style={{ width: '100%', background: 'rgb(var(--aethex-bg))', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 10px', color: 'rgb(var(--aethex-text))', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
                {EXPIRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {/* Max uses */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Max uses</div>
              <select value={maxUses} onChange={e => setMaxUses(Number(e.target.value))}
                style={{ width: '100%', background: 'rgb(var(--aethex-bg))', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 10px', color: 'rgb(var(--aethex-text))', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
                {MAX_USE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {/* Active options summary */}
          <div style={{ marginTop: 10, fontSize: 11, color: 'rgba(255,255,255,0.3)', display: 'flex', gap: 12 }}>
            <span>⏱ Expires: {expiryLabel}</span>
            <span>👥 Uses: {maxUseLabel}</span>
          </div>

          {/* Generate new link */}
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Link expires {expiry === 0 ? 'never' : `in ${expiryLabel.toLowerCase()}`} · {maxUses === 0 ? 'unlimited uses' : `max ${maxUses} use${maxUses !== 1 ? 's' : ''}`}</span>
            <button onClick={handleGenerate} disabled={creating}
              style={{ fontSize: 12, color: 'rgb(var(--aethex-primary))', background: 'none', border: 'none', cursor: 'pointer', padding: 0, opacity: creating ? 0.5 : 1 }}>
              {creating ? 'Generating…' : '↻ Generate new link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
